<template>
  <div class="articles__item">
    <AppLink
      class="articles__item-title"
      :to="computedRoute"
      @click="handleArticleClick"
    >
      <span v-html="article.title" />
      <span v-html="article.tagline" />
    </AppLink>

    <AppLink
      class="articles__item-photo"
      :to="computedRoute"
      @click="handleArticleClick"
    >
      <Picture
        :src="article.lightboxPhotoUrl"
        :src-set="[450, 740, 1080]"
        sizes="(min-width: 320px) and (max-width: 499px) 450px, (min-width: 500px) 700px"
        :alt="article.title"
        loading="lazy"
      />
    </AppLink>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  import { type ArticleSnack } from '@/pages/seoCategoryListing/interface';

  import Picture from './picture.vue';

  interface Props {
    article: ArticleSnack;
  }

  const props = defineProps<Props>();

  const emits = defineEmits<{
    (e: 'article-click', value: ArticleSnack): void;
  }>();

  const handleArticleClick = () => {
    emits('article-click', props.article);
  };
  /**
   * Возвращает url без домена и схемы
   * @param {String} url адрес
   * @return {String}
   */
  const withoutDomain = (url: string) => url.replace(/^https?:\/\/[^/]+/, '');

  const computedRoute = computed(() => (props.article.url ? withoutDomain(props.article.url) : ''));
</script>

<style scoped lang="scss">
  @use '@experience/design-tokens/typography';
  @use '@experience/shared/dist/assets/adaptivity';

  .articles__item {
    display: grid;
    gap: 24px;

    .articles__item-title {
      display: flex;
      flex-direction: column;
      gap: 8px;
      height: min-content;
      color: var(--alias-color-text-default);
      text-decoration: none;
      cursor: pointer;

      span {
        &:first-of-type {
          @include typography.base-700-500-regular;
        }

        &:last-of-type {
          @include typography.base-500-400-regular;
        }
      }

      &:hover + .articles__item-photo :deep(img) {
        transform: scale(1.1);
      }
    }

    .articles__item-photo {
      overflow: hidden;
      border-radius: 8px;
      cursor: pointer;
      transform: translateZ(0);

      :deep(img) {
        object-fit: cover;
        width: 100%;
        height: 100%;
        transition: transform 0.5s ease;
      }

      &:after {
        content: '';
        position: absolute;
        top: 0;
        display: block;
        width: 100%;
        height: 100%;
        border-radius: 8px;
        box-shadow: inset 0 0 0 1px var(--global-color-transparent-black-a050);
      }

      &:hover :deep(img) {
        transform: scale(1.1);
      }
    }

    @include adaptivity.mobile {
      &:first-of-type {
        display: flex;
        flex-direction: column-reverse;
        gap: 12px;
        margin-bottom: 8px;

        .articles__item-photo {
          aspect-ratio: 16/10;
        }
      }

      &:not(:first-of-type) {
        display: flex;
        flex-direction: row-reverse;
        justify-content: flex-end;

        .articles__item-title {
          span {
            &:last-of-type {
              display: none;
            }
          }
        }

        .articles__item-photo {
          width: 70px;
          min-width: 70px;
          max-width: 70px;
          height: 70px;
          min-height: 70px;
          max-height: 70px;

          :deep(img) {
            aspect-ratio: 1/1;
          }
        }
      }
    }
  }

  .articles__item-title {
    @include adaptivity.tablet {
      span {
        &:first-of-type {
          @include typography.base-400-400-regular;
        }

        &:last-of-type {
          @include typography.base-400-400-regular;
        }
      }
    }
  }

  .articles__item:first-of-type .articles__item-title {
    @include adaptivity.tablet {
      span {
        &:first-of-type {
          @include typography.base-600-500-regular;
        }

        &:last-of-type {
          @include typography.base-400-400-regular;
        }
      }
    }
  }
</style>
